/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * WorkOS Authentication Provider
 * 
 * Wraps the WorkOS AuthKitProvider for client-side authentication.
 * This component should wrap the AuthProvider at the app level.
 * 
 * Usage:
 * ```tsx
 * <WorkOSProvider
 *   clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
 *   redirectUri={import.meta.env.VITE_WORKOS_REDIRECT_URI}
 * >
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 * </WorkOSProvider>
 * ```
 */

import React, { createContext, useContext } from 'react';
import { AuthKitProvider } from '@workos-inc/authkit-react';

export interface WorkOSProviderProps {
  children: React.ReactNode;
  clientId?: string;
  /**
   * The redirect URI registered with WorkOS for this application.
   * Must exactly match a redirect URI configured in the WorkOS dashboard.
   * If not provided, the AuthKit SDK falls back to window.location.origin
   * which will fail unless the bare origin is explicitly allowed in WorkOS.
   */
  redirectUri?: string;
}

/**
 * Context to indicate whether WorkOS is available/configured
 */
interface WorkOSAvailabilityContextType {
  isWorkOSAvailable: boolean;
}

const WorkOSAvailabilityContext = createContext<WorkOSAvailabilityContextType>({
  isWorkOSAvailable: false
});

/**
 * Hook to check if WorkOS is available in the current context
 */
export function useWorkOSAvailability(): WorkOSAvailabilityContextType {
  return useContext(WorkOSAvailabilityContext);
}

/**
 * WorkOS Provider component that conditionally wraps children with AuthKitProvider
 * 
 * If no clientId is provided, the children are rendered without the WorkOS provider,
 * allowing the app to function without WorkOS authentication.
 * 
 * Also provides a context that indicates whether WorkOS is available, allowing
 * hooks like useWorkOSAuth to gracefully handle the absence of WorkOS configuration.
 */
export function WorkOSProvider({ children, clientId, redirectUri }: WorkOSProviderProps) {
  if (!clientId) {
    return (
      <WorkOSAvailabilityContext.Provider value={{ isWorkOSAvailable: false }}>
        {children}
      </WorkOSAvailabilityContext.Provider>
    );
  }

  return (
    <WorkOSAvailabilityContext.Provider value={{ isWorkOSAvailable: true }}>
      <AuthKitProvider clientId={clientId} redirectUri={redirectUri}>
        {children}
      </AuthKitProvider>
    </WorkOSAvailabilityContext.Provider>
  );
}

export default WorkOSProvider;
